import { useState, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Wrench } from 'lucide-react';
import { maintenanceApi } from '@/api/maintenance';
import type { Maintenance, MaintenanceStatus } from '@/types';
import DataTable from '@/components/DataTable';
import SearchInput from '@/components/SearchInput';
import Pagination from '@/components/Pagination';
import { formatDate, formatCurrency, formatStatus, getStatusColor, getPriorityColor } from '@/utils/formatters';

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'in_progress', label: 'En Progreso' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];

const typeOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'preventive', label: 'Preventivo' },
  { value: 'corrective', label: 'Correctivo' },
];

export function Maintenance() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const limit = 10;

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['maintenance', page, search, statusFilter, typeFilter],
    queryFn: () => maintenanceApi.getAll({
      page,
      limit,
      search,
      status: (statusFilter || undefined) as MaintenanceStatus | undefined,
      ...(typeFilter ? { type: typeFilter } : {}),
    }),
    placeholderData: keepPreviousData,
  });

  const maintenanceList = response?.data?.data || [];
  const totalPages = response?.data?.totalPages || 1;

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTypeFilter(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      key: 'clientName',
      label: 'Cliente',
      sortable: true,
      render: (item: any) => item.client?.name || item.clientName || '-',
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (item: any) => (
        <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
          {item.type ? formatStatus(item.type) : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      sortable: true,
      render: (item: Maintenance) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(item.status)}`}>
          {formatStatus(item.status)}
        </span>
      ),
    },
    {
      key: 'priority',
      label: 'Prioridad',
      sortable: true,
      render: (item: Maintenance) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPriorityColor(item.priority)}`}>
          {formatStatus(item.priority)}
        </span>
      ),
    },
    {
      key: 'scheduledDate',
      label: 'Fecha Programada',
      sortable: true,
      render: (item: Maintenance) => item.scheduledDate ? formatDate(item.scheduledDate) : '-',
    },
    {
      key: 'assignedTo',
      label: 'Técnico',
      render: (item: any) => item.assignedUser?.name || item.assignedTo || '-',
    },
    {
      key: 'cost',
      label: 'Costo',
      sortable: true,
      render: (item: any) => item.cost != null ? formatCurrency(item.cost) : '-',
    },
  ];

  const errorMessage = error
    ? typeof error === 'string'
      ? error
      : 'Error al cargar los mantenimientos'
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-surface-900">Mantenimientos</h1>
        <button
          type="button"
          onClick={() => navigate('/maintenance/new')}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuevo Mantenimiento
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="w-full sm:max-w-xs">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Buscar mantenimiento..."
          />
        </div>
        <select
          value={statusFilter}
          onChange={handleStatusChange}
          className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={handleTypeChange}
          className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={maintenanceList}
        loading={isLoading}
        error={errorMessage}
        emptyMessage="No hay mantenimientos registrados"
        onRowClick={(item: any) => navigate(`/maintenance/${item.id}`)}
      />

      {!isLoading && maintenanceList.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

export default Maintenance;
